/**
 * Datos de la pantalla de inicio. Compartidos por las dos variantes.
 *
 * El KPI principal es la calidad de la pérdida (p-ratio), no el peso: §5.3 dice que debe ser el
 * número grande de la pantalla de inicio. Y §10 prohíbe destacar un peso "meta", así que acá no
 * hay ninguno.
 */

import { useMemo } from 'react';
import {
  DIAS_ABANDONO,
  adherencia,
  calcularCalendario,
  diffDias,
  estadoPlan,
  evaluarAlertas,
  fechaLarga,
  pRatio,
  proximaSesion,
  resumenSemanal,
  seriesComposicion,
  velocidadPerdida,
  volumenEnVentana,
  type Adherencia,
  type Alerta,
  type CeldaMapaCalor,
  type EntradaCalendario,
  type EstadoPlan,
  type Fecha,
  type PRatio,
  type PlanCalendario,
  type SeriesComposicion,
  type VelocidadPerdida,
} from '@mg/core';

import { useDatos, type Datos } from './useDatos.ts';

/**
 * Qué toca hoy, ya redactado. Vive acá y no en las pantallas porque las dos variantes lo dicen
 * igual: sólo cambia cómo se acomoda en el ancho.
 */
export interface QueTocaHoy {
  titulo: string;
  detalle: string;
  /** El texto del botón. Cambia si hay una sesión abierta: no es lo mismo empezar que retomar. */
  accion: string;
  /**
   * Si la acción merece el botón ámbar. Es falso cuando ya entrenaste o cuando hoy no toca:
   * la franja no puede gritar una acción que no toca (docs/flujo.md §2).
   */
  esPrimaria: boolean;
}

/**
 * Una fila de "Esta semana" con su último estímulo YA REDACTADO.
 *
 * El texto y el tono viven acá y no en las pantallas por dos motivos. Uno es la regla 1: las dos
 * variantes lo dicen igual. El otro es que decidir el tono es una regla de negocio, no un detalle
 * visual — ver `estimulo`.
 */
export interface CeldaSemana extends CeldaMapaCalor {
  estimulo: {
    /** "nunca", "todavía no" o "9 d". */
    texto: string;
    /**
     * Si merece el rojo. Un grupo sólo está abandonado si dejó de entrenarse, y eso no se puede
     * saber de una cuenta que todavía no tiene historial: el primer día de uso `abandonado` viene
     * en true para TODOS los grupos (`diasSinEstimulo === null`), y pintarlos de rojo es retar a
     * alguien por algo que no tuvo tiempo de hacer — justo lo que §10 prohíbe.
     */
    alarma: boolean;
  };
}

export interface DatosInicio {
  datos: Datos;
  pratio: PRatio;
  velocidad: VelocidadPerdida;
  composicion: SeriesComposicion;
  calendario: PlanCalendario | null;
  plan: EstadoPlan | null;
  proxima: EntradaCalendario | null;
  alertas: Alerta[];
  semana: { celdas: CeldaSemana[]; desde: string; hasta: string };
  adherencia: Adherencia;
  volumenSemanaKg: number;
  /** El nombre del día de rutina que toca, resuelto. */
  nombreProxima: string | null;
  queTocaHoy: QueTocaHoy;
  /**
   * Días desde la última comida anotada. `null` si nunca anotó ninguna —de eso se ocupa
   * PrimerosPasos— o si ya anotó algo hoy.
   */
  diasSinAnotarComida: number | null;
  /** Altura pendiente: bloquea el FFMI y se puede derivar del IMC de la balanza. */
  faltaAltura: boolean;
  alturaSugerida: number | null;
}

export function useInicio(): DatosInicio {
  const datos = useDatos();

  return useMemo(() => {
    const altura = datos.perfil?.altura_cm ?? null;

    const pratio = pRatio(datos.medidas, altura, { ventanaDias: 28, hasta: datos.hoy });
    const velocidad = velocidadPerdida(datos.medidas, { ventanaDias: 28, hasta: datos.hoy });
    const composicion = seriesComposicion(datos.medidas, altura, 7);

    const calendario =
      datos.planActivo && datos.gimnasioActivo
        ? calcularCalendario({
            plan: datos.planActivo,
            gimnasio: datos.gimnasioActivo,
            diasBloqueados: datos.diasBloqueados,
            rutinaDias: datos.rutinaDias,
            ejerciciosPorDia: datos.ejerciciosPorDia,
            sesiones: datos.sesiones,
            hoy: datos.hoy,
          })
        : null;

    const alertas = evaluarAlertas({
      hoy: datos.hoy,
      series: datos.contextos,
      ejercicios: datos.ejercicios,
      medidas: datos.medidas,
      alturaCm: altura,
      sexo: datos.perfil?.sexo ?? 'otro',
      calendario,
    });

    const proxima = calendario ? proximaSesion(calendario) : null;
    const nombreProxima = proxima?.nombre ?? null;

    const seriesCompletadasDe = (sesionId: string): number =>
      datos.series.filter((s) => s.sesion_id === sesionId && s.completada).length;

    const sesionDeHoyCompletada = datos.sesionDeHoy?.estado === 'completada' ? datos.sesionDeHoy : null;

    // Los seis estados en los que puede estar un día (docs/flujo.md §2), en orden de prioridad.
    // El tercero —"Ya entrenaste hoy"— es el que faltaba, y sin él la app se contradecía sola:
    // Inicio ofrecía empezar una sesión mientras Plan ya daba el día por gastado.
    const queTocaHoy: QueTocaHoy = ((): QueTocaHoy => {
      if (datos.sesionEnCurso) {
        if (datos.sesionEnCurso.fecha === datos.hoy) {
          const hechas = seriesCompletadasDe(datos.sesionEnCurso.id);
          return {
            titulo: 'Sesión en curso',
            detalle:
              hechas === 0
                ? 'La abriste hoy y todavía no registraste ninguna serie.'
                : `La abriste hoy y llevás ${hechas === 1 ? '1 serie' : `${hechas} series`}.`,
            accion: 'Seguir la sesión',
            esPrimaria: true,
          };
        }
        return {
          titulo: 'Sesión sin cerrar',
          detalle: `Quedó abierta del ${fechaLarga(datos.sesionEnCurso.fecha)}. Se cierra desde ahí mismo.`,
          accion: 'Ir a la sesión',
          esPrimaria: true,
        };
      }

      if (sesionDeHoyCompletada) {
        const hechas = seriesCompletadasDe(sesionDeHoyCompletada.id);
        const partes = [hechas === 0 ? 'Sin series registradas' : hechas === 1 ? '1 serie' : `${hechas} series`];
        // `proxima` es la siguiente de verdad y no hoy: el calendario descarta los días que ya
        // tienen una sesión encima.
        if (proxima) {
          partes.push(`la próxima es el ${fechaLarga(proxima.fecha)}`);
          if (nombreProxima) partes.push(nombreProxima);
        }
        return {
          titulo: 'Ya entrenaste hoy',
          detalle: partes.join(' · '),
          accion: 'Ver la sesión',
          esPrimaria: false,
        };
      }

      if (proxima?.fecha === datos.hoy) {
        return {
          titulo: nombreProxima ?? 'Te toca entrenar',
          detalle: 'Es lo que sigue en tu rutina.',
          accion: 'Ir a entrenar',
          esPrimaria: true,
        };
      }

      // "Hoy no toca" incluye que se puede entrenar igual: la app no es la que decide si
      // alguien va al gimnasio.
      if (proxima) {
        return {
          titulo: 'Hoy no toca',
          detalle: `Según el plan, la próxima es el ${fechaLarga(proxima.fecha)}${
            nombreProxima ? ` · ${nombreProxima}` : ''
          }. Podés entrenar igual.`,
          accion: 'Entrenar igual',
          esPrimaria: false,
        };
      }

      return {
        titulo: 'Cuando quieras',
        detalle: 'Todavía no hay plan cargado, así que abrís una sesión el día que vayas.',
        accion: 'Ir a entrenar',
        esPrimaria: true,
      };
    })();

    // Anotar comida no es un paso de onboarding que se completa una vez: es el dato que se cruza
    // con la masa magra, y los días sin anotar se excluyen del promedio en vez de valer cero. Si
    // hace días que no se anota, la app lo dice —sin reproche, §10— en vez de callarse.
    const ultimaComida = datos.comidas.reduce<Fecha | null>(
      (ultima, c) => (ultima === null || c.fecha > ultima ? c.fecha : ultima),
      null,
    );
    const desdeUltimaComida = ultimaComida === null ? null : diffDias(ultimaComida, datos.hoy);

    // "Esta semana": el último estímulo de cada grupo, redactado con su tono.
    //
    // `abandonado` de core es `diasSinEstimulo === null || diasSinEstimulo >= 14`, así que en una
    // cuenta recién creada TODOS los grupos vienen abandonados y sin fecha. Mostrar eso como
    // "NUNCA" en rojo el primer día es un reproche por algo imposible de haber hecho. El rojo se
    // reserva para lo que sí es un abandono: un grupo que se dejó de entrenar, que sólo se puede
    // afirmar cuando la cuenta lleva más días de historial que los que definen el abandono.
    // Se mira la primera serie COMPLETADA, igual que `seriesEfectivas` de core: una sesión abierta
    // y sin registrar no es historial de entrenamiento.
    const primerEstimulo = datos.contextos.reduce<Fecha | null>(
      (min, c) =>
        !c.serie.completada || c.fecha > datos.hoy ? min : min === null || c.fecha < min ? c.fecha : min,
      null,
    );
    const diasDeHistorial = primerEstimulo === null ? null : diffDias(primerEstimulo, datos.hoy);
    const historialCorto = diasDeHistorial === null || diasDeHistorial < DIAS_ABANDONO;

    const semanaBase = resumenSemanal(
      datos.contextos,
      datos.hoy,
      datos.perfil?.objetivo_series_semanales ?? {},
    );
    const celdasSemana: CeldaSemana[] = semanaBase.celdas.map((c) => ({
      ...c,
      estimulo:
        c.diasSinEstimulo === null
          ? historialCorto
            ? { texto: 'todavía no', alarma: false }
            : { texto: 'nunca', alarma: true }
          : { texto: `${c.diasSinEstimulo} d`, alarma: c.abandonado },
    }));

    // La altura se puede despejar del IMC que ya reportó la balanza, en vez de dejar el FFMI
    // bloqueado esperando que el usuario la busque.
    const conIMC = [...datos.medidas].reverse().find((m) => m.imc !== null && m.peso > 0);
    const alturaSugerida =
      altura === null && conIMC?.imc ? Math.round(Math.sqrt(conIMC.peso / conIMC.imc) * 100) : null;

    return {
      datos,
      pratio,
      velocidad,
      composicion,
      calendario,
      plan: datos.planActivo ? estadoPlan(datos.planActivo, datos.sesiones, datos.hoy) : null,
      proxima,
      alertas,
      semana: { ...semanaBase, celdas: celdasSemana },
      adherencia: adherencia(datos.sesiones, datos.hoy),
      volumenSemanaKg: volumenEnVentana(datos.contextos, datos.hoy, 7, 'kg'),
      nombreProxima,
      queTocaHoy,
      diasSinAnotarComida: desdeUltimaComida !== null && desdeUltimaComida >= 1 ? desdeUltimaComida : null,
      faltaAltura: altura === null,
      alturaSugerida: alturaSugerida !== null && alturaSugerida > 100 && alturaSugerida < 250 ? alturaSugerida : null,
    };
  }, [datos]);
}
